import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { VrboService } from '@app/_services/vrbo.service';
import { VRBOReviewRespondComponent } from '../vrbo-review-respond/vrbo-review-respond.component';
import { VRBORateGuestComponent } from '../vrbo-rate-guest/vrbo-rate-guest.component';

@Component({
    selector: 'app-vrbo-review-detail',
    templateUrl: './vrbo-review-detail.component.html',
    styleUrls: ['./vrbo-review-detail.component.css'],
    providers: [DialogService],
    standalone: false
})
export class VRBOReviewDetailComponent implements OnInit {
  review: any;
  shortTermRentalId: number;

  // Parsed fields
  titleValue = '';
  bodyValue = '';
  overallRating = 0;
  submittedDate = '';
  reviewerName = '';
  reviewerLocation = '';
  guestName = '';
  reservationStatus = '';
  supplierReservationId = '';
  expediaReservationId = '';
  supplierPropertyId = '';
  expediaPropertyId = '';
  responseBody = '';
  responseDate = '';
  responseStatus = '';
  starRatings: Array<{ category: string; value: number }> = [];
  orderUrl: string = null;

  statuses = [
    { name: 'Draft', value: 1 },
    { name: 'New', value: 2 },
    { name: 'Approved', value: 3 },
    { name: 'Rejected', value: 4 },
  ];
  selectedStatus: number;
  statusUpdating = false;

  constructor(
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dialogService: DialogService,
    private messageService: MessageService,
    private vrboService: VrboService
  ) {}

  ngOnInit(): void {
    this.review = this.dynamicDialogConfig.data?.review;
    this.shortTermRentalId = this.dynamicDialogConfig.data?.shortTermRentalId;
    this.parseReview();
  }

  parseReview(): void {
    if (!this.review) return;

    // Review fields (flat ReviewAPI format)
    this.titleValue = this.review.titleValue || this.review.title || '';
    this.bodyValue = this.review.bodyValue || this.review.reviewDesc || '';
    this.overallRating = this.review.overallRating || this.review.rating || 0;
    this.submittedDate = this.review.submittedDateValue || this.review.reviewDate || this.review.createDate || '';

    // Reviewer (flat fields from ReviewAPI)
    this.reviewerName = this.review.reviewerName || [this.review.firstName, this.review.lastName].filter(Boolean).join(' ');
    this.reviewerLocation = [this.review.city, this.review.state, this.review.country].filter(Boolean).join(', ');

    // Reservation
    this.supplierReservationId = this.review.reservationId ? String(this.review.reservationId) : '';

    // Property
    this.supplierPropertyId = this.review.propertyCode || '';

    // Management response (flat field from ReviewAPI)
    if (this.review.responseDesc) {
      this.responseBody = this.review.responseDesc;
    }

    // Order link
    if (this.review.orderId) {
      this.orderUrl = `https://admin.kbmhawaii.com/order/${this.review.orderId}`;
    }

    // Status
    this.selectedStatus = this.review.status;
  }

  getCategoryLabel(category: string): string {
    const labels: { [key: string]: string } = {
      'overall': 'Overall',
      'cleanliness': 'Cleanliness',
      'communication': 'Communication',
      'house_rules': 'House Rules'
    };
    return labels[category] || category;
  }

  openRespondDialog(): void {
    const ref = this.dialogService.open(VRBOReviewRespondComponent, {
      header: 'Respond to Review',
      width: '60%',
      data: { reviewId: this.review.id, shortTermRentalId: this.shortTermRentalId }
    });

    ref.onClose.subscribe((result: any) => {
      if (result?.action === 'RESPONDED') {
        this.dynamicDialogRef.close({ action: 'RESPONDED' });
      }
    });
  }

  openRateGuestDialog(): void {
    const ref = this.dialogService.open(VRBORateGuestComponent, {
      header: 'Rate Guest',
      width: '60%',
      data: {
        shortTermRentalId: this.shortTermRentalId,
        reservationId: this.supplierReservationId,
        guestName: this.guestName || this.reviewerName
      }
    });

    ref.onClose.subscribe((result: any) => {
      if (result?.action === 'RATED') {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Guest rated successfully' });
      }
    });
  }

  onStatusChange(): void {
    if (this.selectedStatus == null || !this.review?.reviewId) return;
    this.statusUpdating = true;
    this.vrboService.updateReviewStatus(this.review.reviewId, this.selectedStatus).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Review status updated successfully' });
        this.statusUpdating = false;
        this.dynamicDialogRef.close({ action: 'STATUS_UPDATED' });
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Failed to update review status' });
        this.statusUpdating = false;
      }
    });
  }

  close(): void {
    this.dynamicDialogRef.close();
  }
}
